import * as React from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import MenuDropdown from '@webapp/controllers/form/menu-dropdown';
import { SortableElement } from 'react-sortable-hoc';
import { MerchProductStatus } from '@services/merch/types/api/merch-product-status';

interface MerchProductDisplayCtrlProps {
  className?: string;
  title: React.ReactNode;
  text: React.ReactNode;
  itemImg: string;
  price: string;
  productionCost: string;
  disablePublish: boolean;
  onSelect: (value: 'edit' | 'publish') => void;
  onTakeDown: () => void;
  status: MerchProductStatus;
}

const MerchProductDisplayCtrl: React.FC<MerchProductDisplayCtrlProps> = ({
  className,
  title,
  text,
  itemImg,
  price,
  productionCost,
  disablePublish,
  onSelect,
  onTakeDown,
  status,
}) => {
  const profitMargin =
    Number(productionCost) <= 0
      ? 0
      : (
          (50 * (Number(price) - Number(productionCost))) /
          Number(productionCost)
        ).toFixed(0);

  const menuOptions = (() => {
    const options = [{ value: 'edit', label: 'Edit' }];
    if (!disablePublish) {
      options.push({ value: 'publish', label: 'Publish' });
    }
    if (status !== 'draft') {
      options.push({ value: 'takedown', label: 'Take down' });
    }
    return options;
  })();

  return (
    <MotionOpacity>
      <Box className={className} hasClose onClose={() => onTakeDown()}>
        <Container
          className="mb-20"
          alignItems="center"
          justifyContent="space-between"
        >
          <InfoContainer alignItems="center" justifyContent="flex-start">
            <MerchSquareImageWrapper>
              <MerchSquareImage src={itemImg} />
            </MerchSquareImageWrapper>
            <InfoWrapper>
              <Typography className="title">{title}</Typography>
              <Typography className="text" type="paragraph" opacity=".5">
                {text}
              </Typography>
            </InfoWrapper>
          </InfoContainer>
          <MenuButton>
            <MenuDropdown
              isSmall
              hasDottedIcon
              mobileTooltip
              menuContent={menuOptions}
              onSelect={(value) => onSelect(value)}
            />
          </MenuButton>
        </Container>
        <InputField
          className="mb-5"
          label={
            <Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.PRICE" />
          }
          value={price}
        />
        <CostInputWrapper>
          <InputField
            className="half"
            label={
              <Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.COST" />
            }
            value={productionCost}
            locked
          />
          <InputField
            className="half"
            label={
              <Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.MARGIN" />
            }
            value={`${profitMargin}%`}
            locked
          />
        </CostInputWrapper>
      </Box>
    </MotionOpacity>
  );
};

export default SortableElement(styled(MerchProductDisplayCtrl)`
  .icon-handle {
    opacity: 0;
    pointer-events: none;
    z-index: 6;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  &:hover .icon-handle,
  .icon-handle.show {
    opacity: 1;
    pointer-events: initial;
  }
`);

const MerchSquareImageWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 2px;
  width: 40px;
  height: 40px;
  position: relative;
  flex-shrink: 0;
  margin-right: 10px;
`;

const MerchSquareImage = styled.img`
  width: 100%;
  max-width: 24px;
  max-height: 24px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Container = styled(Flex)`
  width: 100%;
`;

const InfoContainer = styled(Flex)`
  width: calc(100% - 50px);
`;

const InfoWrapper = styled.div`
  width: calc(100% - 50px);
  .title {
    width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const CostInputWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  flex-direction: column;

  .half {
    flex-grow: 1;
    width: 100%;
    &:first-child {
      margin-bottom: 5px;
    }
  }

  @media ${({ theme }) => theme.device.tablet} {
    flex-direction: row;

    .half {
      width: calc(50% - (5px / 2));

      &:first-child {
        margin-right: 5px;
        margin-bottom: 0;
      }
    }
  }
`;

const MenuButton = styled.button`
  background-color: ${({ theme }) => theme.colors.secondary};
  width: 40px;
  height: 40px;
  margin-left: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  position: relative;
  flex-shrink: 0;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryHover};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;
